/*!
 * egt.content.views — per-kind views for API-published content (spec 35).
 *
 * WP5 ships working, on-brand views for every kind so the publishing pipeline is
 * provable end to end. WP2 (podcast pages) and WP3 (The Making) replace the
 * episode / show / index / journal views with the approved designs; the routing,
 * preview banner and countdown stay as they are.
 *
 * Runs server-side inside the render worker. Every view returns UA/JSON.
 */
var S = require("./egt.sections.js");

var DEFAULT_BG = "/imgs/egt-platform-canvas.jpg";
var TZ = "America/New_York";

function fmtDate(iso, withTime) {
  if (!iso) { return ""; }
  var d = new Date(iso);
  if (isNaN(d.getTime())) { return ""; }
  var opts = { timeZone: TZ, year: "numeric", month: "long", day: "numeric" };
  if (withTime) { opts.hour = "numeric"; opts.minute = "2-digit"; opts.timeZoneName = "short"; }
  return d.toLocaleString("en-US", opts);
}
exports.fmtDate = fmtDate;

/** The logged-in strip: never shown to the public, so plain status words are fine. */
function bannerNode(banner) {
  if (!banner || !banner.text) { return null; }
  return { n: "div", c: "egt-cpreview egt-cpreview--" + (banner.tone || "info"), role: "status",
    i: [ { n: "span", c: "egt-cpreview__dot", "aria-hidden": "true" },
         { n: "span", c: "egt-cpreview__text", i: banner.text } ] };
}

/** Only site paths and absolute URLs are images; bundle-relative paths fall back. */
function img(u) { return (typeof u === "string" && /^(\/|https:\/\/)/.test(u)) ? u : DEFAULT_BG; }

function heroSection(opts) {
  return { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-cpage__hero", i: [
    S.media({ img: img(opts.image), alt: "", priority: true, parallax: 0.06 }),
    { n: "div", c: "egt__shell egt-canvas__inner",
      i: S.glass({ width: opts.width || "narrow", c: opts.c, content: opts.content }) }
  ] };
}

function scripts() {
  // egt.home.js carries the site-wide reveal / parallax behaviour every page uses.
  return [ { n: "script", src: "/src/egt.home.js", type: "text/javascript" },
           { n: "script", src: "/src/egt.content.js", type: "text/javascript", defer: "defer" } ];
}

function wrap(sections, banner, extraTail) {
  var b = bannerNode(banner);
  var list = (b ? [ b ] : []).concat(sections).concat(scripts()).concat(extraTail || []);
  return S.page("/reserve", list);
}

/* ── generic UA/JSON page ─────────────────────────────────────────────────── */
function pageView(data, banner) {
  var p = data.payload || {};
  return wrap([].concat(p.body || []), banner,
    [ S.pageSchema({ path: p.url_path, name: p.title, crumbs: [ { name: p.title } ] }) ]);
}

/* ── countdown (public view of a scheduled item) ──────────────────────────── */
function countdownView(data, banner) {
  var p = data.payload || {}, item = data.item || {};
  var at = item.publish_at || "";
  /* founder 2026-10-26: the pre-release page is a wide, centred card, not a
   * narrow column of text. */
  return wrap([ heroSection({ image: data.extra && data.extra.image, width: "wide", c: "egt-cpage__soon", content: [
    { n: "p", c: "egt__eyebrow egt-reveal", i: data.extra && data.extra.eyebrow ? data.extra.eyebrow : "Coming soon" },
    { n: "h1", c: "egt-phero__title egt-reveal", i: data.extra && data.extra.title ? data.extra.title : (p.title || "Something new") },
    { n: "p", c: "egt-phero__lead egt-reveal", i: "Publishes " + fmtDate(at, true) + "." },
    { n: "div", c: "egt-countdown egt-reveal", "data-egt-countdown": String(at), role: "timer", "aria-live": "off", i: [
      { n: "span", c: "egt-countdown__unit", i: [ { n: "b", "data-u": "d", i: "–" }, { n: "small", i: "days" } ] },
      { n: "span", c: "egt-countdown__unit", i: [ { n: "b", "data-u": "h", i: "–" }, { n: "small", i: "hours" } ] },
      { n: "span", c: "egt-countdown__unit", i: [ { n: "b", "data-u": "m", i: "–" }, { n: "small", i: "minutes" } ] },
      { n: "span", c: "egt-countdown__unit", i: [ { n: "b", "data-u": "s", i: "–" }, { n: "small", i: "seconds" } ] }
    ] }
  ] }) ], banner);
}

var Podcasts = require("./egt.podcasts.views.js");
var Journal = require("./egt.journal.views.js");

var VIEWS = {
  page: pageView,
  countdown: countdownView,
  episode: Podcasts.episode,
  journal_entry: Journal.entry,
  journal_discover: Journal.discover,
  podcasts_index: Podcasts.index,
  podcast_show: Podcasts.show,
  journal_home: Journal.home
};
exports.VIEWS = VIEWS;

exports.render = function render(kind, data, banner) {
  var view = VIEWS[kind];
  if (!view) { return wrap([ heroSection({ content: [ { n: "h1", c: "egt-phero__title", i: "Not available" } ] }) ], banner); }
  return view(data || {}, banner);
};
